import { useMemo } from 'react'
import { Paper, Typography, Box, Skeleton, useTheme } from '@mui/material'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { DailyDashboardStatDto } from '@api/types/dashboard'
import { CHART_COLORS } from './chartConfig'
import { useChartLayout } from './useChartLayout'

interface CompletionsChartProps {
  data?: DailyDashboardStatDto[]
  isLoading?: boolean
}

export function CompletionsChart({ data, isLoading }: CompletionsChartProps) {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const layout = useChartLayout()

  const chartData = useMemo(() => {
    if (!data) return []
    return data.map((item) => ({
      date: new Date(item.date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      }),
      soloCompletions: item.soloCompletions,
      groupCompletions: item.groupCompletions,
    }))
  }, [data])

  const gridColor = isDark ? CHART_COLORS.gridDark : CHART_COLORS.grid
  const axisColor = isDark ? CHART_COLORS.axisDark : CHART_COLORS.axis

  return (
    <Paper sx={{ p: { xs: 1.5, sm: 2 }, height: '100%' }}>
      <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
        Completions
      </Typography>
      {isLoading ? (
        <Box sx={{ height: layout.height }}>
          <Skeleton variant="rectangular" height="100%" />
        </Box>
      ) : (
        <ResponsiveContainer width="100%" height={layout.height}>
          <AreaChart data={chartData} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis dataKey="date" tick={{ fill: axisColor, fontSize: layout.fontSize }} minTickGap={layout.minTickGap} />
            <YAxis tick={{ fill: axisColor, fontSize: layout.fontSize }} width={layout.yAxisWidth} allowDecimals={false} />
            <Tooltip
              contentStyle={{
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
              }}
            />
            <Legend wrapperStyle={{ fontSize: layout.fontSize + 2 }} />
            <Area
              type="monotone"
              dataKey="soloCompletions"
              name="Solo"
              stackId="1"
              stroke={CHART_COLORS.soloCompletions}
              fill={CHART_COLORS.soloCompletions}
              fillOpacity={0.6}
            />
            <Area
              type="monotone"
              dataKey="groupCompletions"
              name="Group"
              stackId="1"
              stroke={CHART_COLORS.groupCompletions}
              fill={CHART_COLORS.groupCompletions}
              fillOpacity={0.6}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </Paper>
  )
}
